/**
 * React Native PassKit Apple Pay for Conekta
 *
 * A React Native library for integrating Apple Pay with Conekta as payment processor
 * using PassKit framework.
 *
 * @author Conekta <alexis.nahuel@conekta.com>
 * @license MIT
 */

import React, { createContext, useContext, PropsWithChildren } from 'react';
import {
  NativeModules,
  TouchableOpacity,
  TouchableOpacityProps,
} from 'react-native';

const { ApplePayModule } = NativeModules;

/**
 * Apple Pay payment method information as returned by PassKit
 */
export interface ApplePayPaymentMethod {
  displayName: string;
  network: string;
  type: string;
}

/**
 * Apple Pay payment data header containing cryptographic information
 * Required for Conekta's Apple Pay token validation
 */
export interface ApplePayPaymentDataHeader {
  publicKeyHash: string;
  ephemeralPublicKey: string;
  transactionId: string;
}

/**
 * Apple Pay payment data containing encrypted payment information
 */
export interface ApplePayPaymentData {
  data: string;
  signature: string;
  header: ApplePayPaymentDataHeader;
  version: string;
}

/**
 * Complete Apple Pay token structure for Conekta integration
 * This token should be sent directly to Conekta's API for processing
 */
export interface ApplePayToken {
  paymentData: ApplePayPaymentData;
  paymentMethod: ApplePayPaymentMethod;
  transactionIdentifier: string;
}

/**
 * Configuration context for Apple Pay integration with Conekta
 */
interface ApplePayContextType {
  merchantIdentifier: string; // Must be registered with Conekta
  supportedNetworks: string[]; // Networks supported by Conekta in your region
  countryCode: string; // LATAM country code (e.g., "MX", "CO", "PE")
  currencyCode: string; // Local currency code (e.g., "MXN", "COP", "PEN")
}

interface ApplePayProviderProps extends ApplePayContextType {
  // @todo: Add support for Conekta-specific merchant validation,
  // shipping methods and different merchant capabilities
}

/**
 * Payment summary for display in Apple Pay sheet
 * Amount should be in the smallest currency unit (e.g., cents for MXN)
 */
interface PaymentSummary {
  label: string;
  amount: string; // @todo: revisar si es string o integer
}

interface ApplePayButtonProps extends TouchableOpacityProps {
  /**
   * Payment details for the transaction
   */
  payment: PaymentSummary;
  /**
   * Callback executed when payment is authorized and token is received
   * The application is responsible for processing the token with Conekta's API
   */
  onPaymentToken: (token: ApplePayToken) => void;
  /**
   * Optional callback executed when payment initiation fails or user cancels
   */
  onError?: (error: Error) => void;
  children: React.ReactNode;
}

const ApplePayContext = createContext<ApplePayContextType | undefined>(
  undefined,
);

/**
 * Provider component that configures global Apple Pay parameters for Conekta integration
 * All ApplePayButton components within this provider will use these settings
 *
 * @param merchantIdentifier - Your merchant ID registered with Conekta
 * @param countryCode - Country code where your business operates
 * @param currencyCode - Local currency code for your market
 * @param supportedNetworks - Payment networks supported by Conekta
 */
export const ApplePayProvider: React.FC<
  PropsWithChildren<ApplePayProviderProps>
> = ({
  children,
  merchantIdentifier,
  supportedNetworks,
  countryCode,
  currencyCode,
}) => {
  const contextValue = {
    merchantIdentifier,
    supportedNetworks,
    countryCode,
    currencyCode,
  };

  return (
    <ApplePayContext.Provider value={contextValue}>
      {children}
    </ApplePayContext.Provider>
  );
};

/**
 * Hook to access Apple Pay configuration context
 * Must be used within an ApplePayProvider
 */
export const useApplePayContext = () => {
  const context = useContext(ApplePayContext);
  if (!context) {
    throw new Error(
      'useApplePayContext must be used within an ApplePayProvider',
    );
  }
  return context;
};

/**
 * A wrapper component that converts its children into an Apple Pay button
 * Initiates the native payment flow when pressed and returns a token
 *
 * The token returned by this component should be sent directly to your
 * backend for processing with Conekta's charges/orders API
 */
export const ApplePayButton: React.FC<ApplePayButtonProps> = ({
  payment,
  onPaymentToken,
  onError,
  children,
  ...rest
}) => {
  const context = useApplePayContext();

  const handlePress = async () => {
    if (!payment) {
      const error = new Error('ApplePayButton: payment prop is required');
      onError?.(error);
      return;
    }

    try {
      const paymentRequest = {
        ...context,
        label: payment.label,
        amount: payment.amount,
      };

      const token: ApplePayToken = await ApplePayModule.pay(paymentRequest);
      onPaymentToken(token);
    } catch (error: any) {
      onError?.(new Error(error.message));
    }
  };

  return (
    <TouchableOpacity onPress={handlePress} {...rest}>
      {children}
    </TouchableOpacity>
  );
};

/**
 * Completes the Apple Pay transaction from the JavaScript side
 * Must be called after Conekta has processed the payment
 *
 * This function signals to the native Apple Pay UI whether the payment
 * succeeded or failed, allowing the user to see the appropriate feedback
 *
 * @param success - true if Conekta payment was successful, false otherwise
 * @param errorMessage - Error message to display if success is false
 */
export const completeApplePayPayment = async (
  success: boolean,
  errorMessage: string | null = null,
): Promise<void> => {
  try {
    await ApplePayModule.completePayment(success, errorMessage);
  } catch (error: any) {
    // @todo: Add integration with Conekta's error tracking
    throw new Error(`Failed to complete Apple Pay payment: ${error.message}`);
  }
};
